import { NO_BG_USAGE_KEY } from './constants';

export function computeHasCommitment(row) {
  return (
    row.work_status !== 'cancelled' && // ยกเลิก = ไม่มีภาระผูกพันอีกต่อไป
    (row.work_status !== 'done' ||
      row.finance_status !== 'completed_po' ||
      row.legal_warranty ||
      row.legal_debt ||
      row.legal_lawsuit ||
      row.report_status === 'pending') // ต้องส่งรายงานแต่ยังไม่ได้ส่ง = ยังมีภาระผูกพัน
  );
}

// มีความคืบหน้า > 0 แต่ยังไม่ได้แก้ "ด้านงาน" ให้ตรง (ยังรอเริ่ม/ไม่ระบุ หรือดำเนินการแต่ยังไม่ระบุว่าใครทำ)
// กฎเดียวกับ backend patchDashboard — ห้ามใส่ % ถ้าด้านงานไม่ใช่ done หรือ in_progress ที่ระบุผู้ทำแล้ว
export function hasProgressWorkConflict(row) {
  if ((Number(row?.progress) || 0) <= 0) return false;
  const ws = row?.work_status || '';
  if (ws === 'done' || ws === 'cancelled') return false;
  if (ws === 'in_progress') return !row?.work_method;
  return true;
}

export function isBgUsageDisabled(row) {
  return row?.bg_usage_category === NO_BG_USAGE_KEY;
}

// BG หลังปรับ = BG Remain now (col10) − AP PENDING ในงบ + รอเงินคืน — ปรับตรงนี้ครั้งเดียวตอนรวม ไม่ซ่อนอยู่ใน
// Paid/BG Remain now/ตารางต่องบย่อย (ทุกตัวนั้นเป็นยอดดิบ) ใช้ทั้งใน BGเหลือที่มีสิทธิจ่าย (col12) และ BG คงเหลือ/เกิน BG
export function getBgAfterAdjust(row) {
  return (
    (Number(getProjectedFund(row)) || 0) -
    getApPendingIncluded(row) +
    getRefundPendingValue(row)
  );
}

// col 12 (P111): BG เหลือที่มีสิทธิจ่าย
// ถ้าเลือก "ไม่ใช้ BG แล้ว" ให้เป็น 0, ไม่งั้นใช้ max(BG หลังปรับ, 0)
export function getPayableBgEligible(row) {
  if (isBgUsageDisabled(row)) return 0;
  return Math.max(getBgAfterAdjust(row), 0);
}

// AP Pending (pending_items ยังไม่จ่าย) เฉพาะรายการที่ติ๊ก "รวมในสรุปอนาคต" (include_in_future !== false;
// รายการเก่าที่ไม่มี field = รวม) — ใช้แทน ap_pending_unpaid ในสูตร col17 (สรุปอนาคตต้องจ่ายอีกรวม) และ
// กำไรคาดการณ์ (getSpentValue / realProfit ใน T-1) ส่วนแถว AP PENDING ที่แสดงผลยังโชว์ยอดรวมทุกรายการเหมือนเดิม
// ถ้าไม่มี row.budget (ไม่มีรายการให้แจกแจง) ถอยกลับไปใช้ ap_pending_unpaid ที่ backend คำนวณ
export function getApPendingIncluded(row) {
  if (!Array.isArray(row?.budget)) return Number(row?.ap_pending_unpaid) || 0;
  return row.budget.reduce(
    (acc, b) =>
      acc +
      (b?.pending_items || []).reduce(
        (s, i) =>
          s + (!i?.paid && i?.include_in_future !== false ? Number(i?.amount) || 0 : 0),
        0,
      ),
    0,
  );
}

// AP Pending ทุกรายการที่ยังไม่จ่าย (ไม่สน "อยู่ในงบ") = ค่าที่แถว 10 (P111) แสดง / ap_pending_unpaid ของ backend
// ใช้ในแถว 14 (Total Payable) — ส่วน "อยู่ในงบ" (getApPendingIncluded) มีผลแค่แถว 13 (หักจาก BG ที่เหลือ)
export function getApPendingAll(row) {
  if (!Array.isArray(row?.budget)) return Number(row?.ap_pending_unpaid) || 0;
  return row.budget.reduce(
    (acc, b) =>
      acc +
      (b?.pending_items || []).reduce(
        (s, i) => s + (!i?.paid ? Number(i?.amount) || 0 : 0),
        0,
      ),
    0,
  );
}

export function getBgColumnValues(row) {
  const bgUsageDisabled = isBgUsageDisabled(row);
  const col32 = getApPendingIncluded(row);
  const col16 = Number(row?.to_spend) || 0;
  const col12 = getPayableBgEligible(row);
  // col17 (P111 row14) = row8 + row10 + row11 + row13 − row12
  // row12 = รอเงินคืน (getRefundPendingValue) เงินที่จะได้คืนในอนาคต หักออกจากยอดที่ต้องจ่ายอีก
  // row8 = รายการตั้งเบิกที่รอจ่าย (getExpectedValue) ยังไม่จ่ายแต่ถือเป็นอนาคตต้องจ่าย
  // + AP Pending ทุกรายการที่ยังไม่จ่าย(getApPendingAll = แถว 10) + ต้องใช้อีก(col16) + BGเหลือที่มีสิทธิจ่าย(col12)
  // (col12 มีแถว 8 และ AP ที่ติ๊ก "อยู่ในงบ" หักออกจาก BG ที่เหลือแล้ว — ไม่นับซ้ำ เพราะบวกกลับที่แถว 8/10 พอดี)
  // (รอจ่าย/col35 ไม่ได้บวกตรงนี้ — ย้ายไปหักรวมอยู่ใน getProjectedFund/col10 แทน)
  // (รอเงินคืน/col34 และ AP ในงบ ปรับใน getBgAfterAdjust ที่เดียว — ไม่ซ่อนใน Paid/BG Remain now)
  const col17 =
    getExpectedValue(row) +
    getApPendingAll(row) +
    col16 +
    col12 -
    getRefundPendingValue(row);
  // ธงเกิน/คงเหลือ BG (row 20/21) ไม่รวม AP เข้า col17 เพราะ col12 หัก AP ไปแล้วใน getBgAfterAdjust
  const raw = getBgAfterAdjust(row) - (col16 + col12); // (row9 − AP ในงบ + row12) − (row11 + row13)
  // col15 "BG คงเหลือ" = raw เมื่อ >= 0, col14 "เกิน BG" = raw เมื่อติดลบ
  const col15 = Math.max(raw, 0);
  const col14 = raw < 0 ? raw : null;
  return { bgUsageDisabled, col32, col12, col15, col14, col16, col17 };
}

// col 11 "ใช้ไปแล้ว" = ยอด Paid เท่านั้น (expenses ที่ status === 'SUCCESS')
// เหมือนแถวรวมคอลัมน์ "Paid" ใน BudgetTable.js (renderCost) เป๊ะ — BudgetTable รวมยอดผ่าน budget
// ของโปรเจกต์เท่านั้น (ไม่ใช่ expenses ทั้งหมดที่ project ตรงกัน) เลยต้องกรอง e.budget ให้ตรงกับ
// budget จริงของโปรเจกต์นี้ด้วย ไม่งั้นถ้ามี Expense ที่ budget เป็น null/ผูกผิดโปรเจกต์ (data ไม่สมบูรณ์)
// ตัวเลขจะไม่ตรงกับ BudgetTable ทันที (ไล่แก้ data ทีละเคสไม่จบ เพราะเป็นบั๊กที่จุดสร้าง Expense เอง)
// ไม่หักเงินรอคืน (refund_items ยังไม่คืน, col34) ที่นี่ — Paid = ยอดจ่ายสำเร็จเต็มจำนวน ตรงกับ renderCost ใน
// BudgetTable.js เสมอ ส่วนเงินรอคืนไปหักที่ "ยอดรวมใช้ไป" แทน (Grand Total ใน BudgetTable; ในแดชบอร์ดคือ
// getBgAfterAdjust (BG) และ getSpentValue/กำไรกรณีฟ้องร้อง ผ่าน getNetPaidValue) ห้ามหักซ้ำที่อื่น
export function getPaidValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  const paidGross = (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (e?.status === 'SUCCESS' && budgetIds.has(String(e?.budget))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
  return paidGross;
}

// ยอดรวมใช้ไป/ภาระ = Paid + Expected + AP Pending ในงบ (เฉพาะที่ติ๊กรวมในสรุป) − เงินรอคืน
// (เหมือน Grand Total ใน BudgetTable.js แต่ AP Pending นับเฉพาะรายการที่ติ๊กรวมในสรุป) — ใช้ในกราฟ POOverview
export function getCommittedCostValue(row) {
  return (
    getPaidValue(row) +
    getExpectedValue(row) +
    getApPendingIncluded(row) -
    getRefundPendingValue(row)
  );
}

// Paid เต็มจำนวน − เงินรอคืน — ใช้ในสูตรคำนวณ (BG Remain now / กำไร) ที่ต้องไม่นับเงินรอคืนเป็นต้นทุน
export function getNetPaidValue(row) {
  return getPaidValue(row) - getRefundPendingValue(row);
}

// col 35 "รอจ่าย" = ยอด expense ที่ยังไม่จ่าย/ไม่ถูกปฏิเสธ (status ไม่ใช่ SUCCESS และไม่ใช่ REJECT)
// เหมือนคอลัมน์ "Expected" ใน BudgetTable.js (renderCostApprove) เป๊ะ — กรอง e.budget ให้ตรงกับ budget
// จริงของโปรเจกต์นี้เหมือน getPaidValue ด้านบน (เหตุผลเดียวกัน)
// สถานะที่ไม่ใช่ "รอจ่าย": จ่ายแล้ว / ปฏิเสธ / ยกเลิก (ถังขยะ) / ฉบับร่างค่าแรงรุ่นเก่า — ต้องแก้คู่กับ backend/services/projectDashboardFormulas.js
export const EXCLUDED_FROM_EXPECTED = ['SUCCESS', 'REJECT', 'CANCEL', 'LABOR_DRAFT'];

export function getExpectedValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  // Expenses ที่ยังมี Budget.pending_items entry ผูกอยู่ (source_expense_id) ถูกนับผ่าน "AP Pending"
  // (ap_pending_unpaid) แทนแล้ว — ข้ามไม่นับซ้ำที่นี่ กันเงินก้อนเดียวกันถูกหักออกจากเงินคงเหลือสองรอบ (ผ่าน
  // Expected/col35 ที่นี่ กับผ่าน AP Pending/col32 พร้อมกัน) ดู upsertLaborExpenseLocked ใน expenses.service.js
  // ที่ดูแล mirror นี้ (สร้าง/แก้ตอนยังเป็น PENDING, ลบทันทีที่สถานะไม่ใช่ PENDING แล้ว — พอลบ mirror ปุ๊บ
  // รายการนี้ก็เริ่มถูกนับตรงนี้แทนเองอัตโนมัติ ไม่มีช่วงที่นับซ้ำหรือขาดหาย) ต้องแก้คู่กับ renderCostApprove
  // ใน BudgetTable.js เสมอ (ของซ้ำกันคนละไฟล์ ดู CLAUDE.md)
  const apPendingLinkedIds = new Set(
    (row?.budget || []).flatMap((b) =>
      (b?.pending_items || [])
        .filter((i) => i?.source_expense_id)
        .map((i) => String(i.source_expense_id)),),
  );
  return (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (!EXCLUDED_FROM_EXPECTED.includes(e?.status) &&
      budgetIds.has(String(e?.budget)) &&
      !apPendingLinkedIds.has(String(e?._id || e?.id))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
}

// col 10 "ทุนคาดการณ์" = col 33 (BG / total_budget) - col 11 (ใช้ไปแล้ว / Paid) - col 35 (รอจ่าย / Expected)
export function getProjectedFund(row) {
  return (
    (Number(row?.total_budget) || 0) - getPaidValue(row) - getExpectedValue(row)
  );
}

// col 34 "รอเงินคืน" = ผลรวม refund_items ที่ยังไม่คืน (refunded !== true) ของทุก budget ในโปรเจกต์นี้
// เหมือนคอลัมน์ "รอเงินคืน" ใน BudgetTable.js (renderRefundPending) รวมทุก budget
export function getRefundPendingValue(row) {
  return (row?.budget || []).reduce((acc, b) => {
    const unrefunded = (b?.refund_items || []).filter((i) => !i?.refunded);
    return (
      acc + unrefunded.reduce((s, i) => s + (Number(i?.amount) || 0), 0)
    );
  }, 0);
}

// ต้นทุนรวมที่ใช้คิดกำไรคาดการณ์ = row7 (BG PAID เต็มจำนวน) + row14 (สรุปอนาคตต้องจ่ายอีกรวม)
// ตรงกับป้ายสูตรแถว 22 ใน ProjectSingleDashboard.js (T-1): "= 1 − 7 − 14"
// ใช้เป็นส่วนประกอบของ col 19 "กำไรคาดการณ์" (getEstimatedProfit ด้านล่าง) เท่านั้น
function getSpentValue(row) {
  return getPaidValue(row) + getBgColumnValues(row).col17;
}

// col 19 "กำไรคาดการณ์" — สูตรเดียวกับ row22Profit ใน ProjectSingleDashboard.js (T-1):
// ปกติ = po (หัก VAT ถ้า include_vat, ใช้ getPoNetOfVat) - จ่ายแล้ว(getSpentValue)
// (เดิมตอนปิดใช้ BG แล้ว เคยบวกผลรวมงบเหลือเป็นบวกของแต่ละ budget (positiveRemainingSum) กลับเข้าไปเป็น
// กำไรเพิ่ม ทำให้ P5/T-1 ตัวเลขไม่ตรงกัน — ตัดออกแล้ว, เดิมใช้ row.cost ดิบไม่หัก VAT ด้วย — เปลี่ยนเป็น
// getPoNetOfVat ให้ตรงกับ T-1 ตามที่ user ยืนยัน 2026-08-07)
// ถ้าโปรเจกต์ถูกฟ้องร้องแล้ว (legal_lawsuit) งานหยุดชะงัก ใช้สูตรง่ายๆ รับเงินมาแล้ว(received+seed_received)
// - จ่ายไปแล้ว(getPaidValue) แทน ไม่นับส่วนที่ยังไม่เกิดขึ้นจริง (รอจ่าย/ต้องใช้อีก/ฯลฯ) — เหมือน T-1
// เป๊ะ (เดิม P5 ไม่มีเงื่อนไขนี้เลย เพิ่งเพิ่มพร้อมกับจุด VAT ด้านบน 2026-08-07)
export function getEstimatedProfit(row) {
  if (row?.legal_lawsuit) {
    const receivedCombined =
      (Number(row?.received) || 0) + (Number(row?.seed_received) || 0);
    return receivedCombined - getNetPaidValue(row);
  }
  return getPoNetOfVat(row) - getSpentValue(row);
}

// col 18 "Profit Target" — สูตรเดียวกับ targetProfit ใน ProjectSingleDashboard.js (P111 num 5) = cost - BG ที่ตั้งไว้
export function getTargetProfit(row) {
  return (Number(row?.cost) || 0) - (Number(row?.total_budget) || 0);
}

// PO (col 6) หัก VAT ถ้า include_vat — สูตรกลางที่ใช้ร่วมกันทั้ง filter/sort/totals/Excel export/render
export function getPoNetOfVat(row) {
  return row?.include_vat
    ? (row?.cost || 0) - (row?.vat || 0)
    : row?.cost || 0;
}
